import axios from 'axios';
import config from '../config.js';
import AuthService from './authService'
import queryString from 'query-string';

// All SyteLine calls go through the ION API gateway. The Authorization (Bearer) and
// X-Infor-MongooseConfig headers are set by AuthService.
axios.defaults.baseURL = config.restBaseUrl;

let logoutUrl = '/login';
if (process.env.PUBLIC_URL) {
    logoutUrl = process.env.PUBLIC_URL + logoutUrl;
}

// The sign-in callback page reports its own errors instead of bouncing to /login
function onCallbackPage() {
    return window.location.pathname.endsWith('/callback');
}

axios.interceptors.response.use(function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    if (AuthService.getToken()) {
        if (typeof response === "object") {
            let message = '';
            try {
                message = response.data.Message.toLowerCase();
            }
            catch (error) { }

            if (response.data.MessageCode === 300 ||
                message.toLowerCase().includes('error authenticating') ||
                message.toLowerCase().includes('Your session has been deleted')) {
                // Bad token
                if (!onCallbackPage()) {
                    AuthService.setSignOutReason(`${response.config.url} returned MessageCode ${response.data.MessageCode}: ${response.data.Message}`);
                    AuthService.logout();
                    window.location.pathname = logoutUrl;
                }
                return Promise.reject(response);
            }
            else if (response.data.MessageCode >= 301) {
                // TODO: Should show error page - something else went terribly wrong
                return Promise.reject(response);
            }
        }
    }

    return response;

}, function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    let message = "";

    try {
        message = error.response.data.message.toLowerCase();
    }
    catch (error) { }

    // ION API answers 401 when the OAuth token is missing or expired
    let status = error.response ? error.response.status : 0;

    if ((status === 401 || message.toLowerCase().includes('error authenticating')) && !onCallbackPage()) {
        let url = error.config ? error.config.url : '';
        let body = error.response ? JSON.stringify(error.response.data) : '';
        AuthService.setSignOutReason(`${url} returned HTTP ${status}: ${body}`);
        AuthService.logout();
        window.location.pathname = logoutUrl;
    }
    return Promise.reject(error);
});

function getProjectDashboardData(projNum) {
    let properties = ['ProjNum','Data','CreatedBy','UpdatedBy','_ItemId','CreateDate','RecordDate'];

    return callSytelineIDO('ue_WMS_Dashboards', properties, projNum);
}

function updateDashboardData(projNum, data, itemId) {
    let payload = [];

    if (itemId) {
        // Update
        payload.push({
            "Action": 2,
            "ItemId": itemId,
            "Properties": [
                {
                    "IsNull": false,
                    "Modified": true,
                    "Name": "Data",
                    "Value": JSON.stringify(data)
                }
            ]
        })
        console.debug('will update to ue_WMS_Dashboards', payload);
    
        return putToSyteline('ue_WMS_Dashboards', 'updateitems', payload);
    }
    else {
        payload.push({
            "Action": 1,
            "ItemId": "PBT=[ue_WMS_Dashboard]",
            "Properties": [
                {
                    "IsNull": false,
                    "Modified": true,
                    "Name": "ProjNum",
                    "Value": projNum
                },
                {
                    "IsNull": false,
                    "Modified": true,
                    "Name": "Data",
                    "Value": JSON.stringify(data)
                }
            ]
        })
        console.debug('will insert to ue_WMS_Dashboards', payload);
    
        return postToSyteline('ue_WMS_Dashboards', 'additems', payload);
    }
}

function getProjectsList() {
    let properties = ['ProjNum', 'ProjMgr', 'ProjDesc', 'Stat', 'CurrCode'];

    return callSytelineIDO('SLProjs', properties, null, 'ProjDesc');
}

function getProjectCodes(projNum) {
    let properties = ['description', 'cost_code', 'proj_num'];

    return callSytelineIDO('ue_WMS_ProjectCostCodes', properties, null, null, `proj_num = '${projNum}'`);
}

function getProjectCommittedCosts(projNum) {
    let properties = ['ProjNum', 'CostCode', 'RefNum', 'RefLineSuf', 'Item', 'ItemDesc','RefRelease'];

    return callSytelineIDO('SLProjMatls', properties, null, null, `RefType='P' AND ProjNum = '${projNum}'`);
}

// Dashboard
// Dashboard
// Replicates the GDL_Rpt_ProjectCostDetail DataView by joining 3 IDOs:
//   1. SLProjs (resolves to ue_WMS_SLProjs) - project info
//   2. SLProjCosts (resolves to ue_WMS_SLProjCost) - cost data, filtered TaskNum=0
//   3. ue_WMS_ProjectCostCodes - cost code descriptions, linked by CostCode + ProjNum
function getProjectCostDetailReport(projNum) {
    let costProperties = ['ProjNum', 'CostCode', 'TaskNum', 'Type', 'WMS_DerCostCodeDescription', 'ACost', 'DerOPNR', 'DerOPNRExch', 'DerFataCommittedCost', 'DerFataCommittedCostExch', 'EstimatedFinalCost', 'AmountToSpend', 'DerETVariance', 'PlanCost',
        'DerTotalRcvdCostExchVol', 'DerTotalOrderedExchVol'];

    let projProperties = ['ProjNum', 'ProjMgr', 'ProjDesc'];

    return callSytelineIDO('SLProjCosts', costProperties, projNum, 'CostCode', "Type='P' AND TaskNum = 0")
        .then(costs => {
            return callSytelineIDO('SLProjs', projProperties, projNum)
                .then(projects => {
                    return callSytelineIDO('ue_WMS_ProjectCostCodes', ['description', 'cost_code', 'proj_num'], null, null, `proj_num = '${projNum}'`)
                        .then(costCodes => {
                            let proj = projects[0] || {};

                            let codeDescMap = {};
                            costCodes.forEach(cc => { codeDescMap[cc.cost_code] = cc.description; });

                            const results = costs.map(cost => ({
                                ...cost,
                                ProjMgr: proj.ProjMgr,
                                ProjDesc: proj.ProjDesc,
                                CostCodeDescription: codeDescMap[cost.CostCode] || cost.WMS_DerCostCodeDescription || ''
                            }));

                            return results;

                            // const totalOPNR = results.reduce((sum, cost) => sum + (parseFloat(cost.DerOPNR) || 0), 0);
                            // const totalOPNRExch = results.reduce((sum, cost) => sum + (parseFloat(cost.DerOPNRExch) || 0), 0);

                            // const stats = { items: results, totalOPNR, totalOPNRExch };
                            // console.log('getProjectCostDetailReport', stats);
                            // return stats;
                        });
                });
        });
}

// Legacy version that does not use linked queries per GDL_Rpt_ProjectCostDetail DataView
// function getProjectCostDetailReport(projNum) {
//     let properties = ['ProjNum', 'CostCode', 'TaskNum', 'Type', 'WMS_DerCostCodeDescription', 'ACost', 'DerOPNR', 'DerOPNRExch', 'DerFataCommittedCost','DerFataCommittedCostExch', 'EstimatedFinalCost', 'AmountToSpend', 'DerETVariance', 'PlanCost', 
//         'DerTotalRcvdCostExchVol', 'DerTotalOrderedExchVol'];

//     //return callSytelineIDO('SLProjCosts', properties, projNum, 'CostCode', 'TaskNum = 0');
//     return callSytelineIDO('SLProjCosts', properties, projNum, 'CostCode', 'Type=\'P\' AND TaskNum = 0');  // Fix for 22111 issue?
// }

function getProjectTaskTens(projNum) {
    let properties = ['ProjNum', 'CostCode', 'TaskNum', 'Type', 'WMS_DerCostCodeDescription', 'ACost', 'DerOPNR', 'DerFataCommittedCost','DerFataCommittedCostExch', 'EstimatedFinalCost', 'AmountToSpend', 'DerETVariance', 'PlanCost', '_ItemId'];

    return callSytelineIDO('SLProjCosts', properties, projNum, 'CostCode', '(TaskNum = 0 OR (TaskNum = 10 AND Type=\'T\'))');
}

function getProjectBillingsReport(projNum) {
    let properties = ['ProjNum', 'ProjDesc', 'ProjMgr', 'Amount', 'AmountPaid', 'Outstanding', 'PctOutstanding', 'InvoiceNum', 'InvoiceDate'];

    return callSytelineCLM('ue_WMS_DashProjectBillings', properties, null, null, 'WMS_CLM_ProjectBillings', projNum + ',');
}

function getProjectPOReport(projNum) {
    let properties = ['ProjNum', 'ProjDesc', 'ProjName', 'AmountBilled', 'OpenBalance', 'PONum', 'POStatus', 'PODate', 'ProjectID', 'POStat', 'POAmount'];

    return callSytelineCLM('ue_WMS_DashProjectPOs', properties, null, 'PODate,PONum', 'WMS_CLM_ProjectPOs', projNum + ',');
}

// OPNR
function getPOItems(projNum) {
    let properties = ['PoOrderDate', 'DerExtItemCostConvExc', 'DerOPNR', 'DerExtPlanCostConv', 'DerTotalRcvdCost', 'DerExtPlanCostConvExc', 'DerTotalRcvdCostExc', 'DerVendInvNum', 'PoVendNum', 'UbCostCode', 'PoStat', 
    'PoNum', 'PoVendorPo', 'PoLine', 'VenadrName', 'DerRefDescription', 'Description', 'RefNum', 'PoRelease', 'ItmContRefType', 'RefType', 'DerStatFormatted', 'Item'];

    return callSytelineIDO('SLPoItems', properties, null, 'PoNum,PoLine', `RefNum LIKE '%${projNum.trim()}%'`);
}

function getPOItemsForOPNR(projNum) {
    let properties = ['PoOrderDate', 'DerExtItemCostConvExc', 'DerOPNR', 'DerExtPlanCostConv', 'DerTotalRcvdCost', 'DerExtPlanCostConvExc', 'DerTotalRcvdCostExc', 'DerVendInvNum', 'PoVendNum', 'UbCostCode', 'PoStat',
    'PoNum', 'PoVendorPo', 'PoLine', 'VenadrName', 'DerRefDescription', 'Description', 'RefNum', 'PoRelease', 'ItmContRefType', 'RefType', 'DerStatFormatted', 'Item',
    'QtyOrdered', 'QtyReceived', 'ItemCost'];

    return callSytelineIDO('SLPoItems', properties, null, 'PoNum,PoLine', `RefNum LIKE '%${projNum.trim()}%'`);
}

function getMexicanExchangeRates() {
    let properties = ['EffDate', 'FromCurrCode', 'ToCurrCode', 'CurrEuroRate', 'CurrRateIsDivisor', 'CurrPartOfEuro', 'BuyRate', 'SellRate'];

    return callSytelineIDO('SLCurrates', properties, null, 'EffDate DESC', "FromCurrCode='USD' AND ToCurrCode='MXN'");
}

function invokeProjectTaskBudgetRollup(projNum) {
    console.debug('ROLLUP!')
    // C# IDO method (replaces the on-prem SP! call to GDL_RollProjectTaskBudgetSp); parameters: ProjNum, Infobar
    return invokeMethodSyteline('ue_WMS_Dashboards', 'WMS_RollProjectTaskBudget', projNum + ',')
        .then((response) => {
            if (response.data && response.data.ReturnValue && response.data.ReturnValue !== '0') {
                let message = response.data.Parameters ? response.data.Parameters[1] : 'Task budget rollup failed';
                return Promise.reject(message);
            }
            return response;
        });
}

function deleteEstimatedFinalCostItems(items)
{
    let payload = [];

    items.forEach((item) => {
        payload.push({
            "Action": 4,
            "ItemId": item._ItemId,
            "Properties": [
                {
                    "IsNull": false,
                    "Modified": false,
                    "Name": "_ItemId",
                    "Value": null
                }
            ]
        })
    })

    if (payload.length > 0) {
        console.debug('will delete to SLProjCosts', payload);
        return deleteSyteline('SLProjCosts', 'deleteitems', payload);
    }
    else {
        return Promise.resolve();
    }
}

function updateEstimatedFinalCostItems(items)
{
    let payload = [];

    items.forEach((item) => {
        if (item) {
            payload.push({
                "Action": 2,
                "ItemId": item._ItemId,
                "Properties": [
                    {
                        "IsNull": false,
                        "Modified": true,
                        "Name": "EstimatedFinalCost",
                        "Value": item.amount
                    }
                ]
            })
        }
    })

    if (payload.length > 0) {
        console.debug('will update to SLProjCosts', payload);
        return putToSyteline('SLProjCosts', 'updateitems', payload);
    }
    else {
        return Promise.resolve();
    }

}

function insertEstimatedFinalCostItems(projNum, items)
{
    let insertPayload = [];

    items.forEach((item) => {
        if (item) {
            insertPayload.push({
            "Action": 1,
            "ItemId": "PBT=[projcost]",
            "Properties": [
                    {
                        "IsNull": false,
                        "Modified": true,
                        "Name": "ProjNum",
                        "Value": projNum
                    },
                    {
                        "IsNull": false,
                        "Modified": true,
                        "Name": "CostCode",
                        "Value": item.costCode
                    },
                    {
                        "IsNull": false,
                        "Modified": true,
                        "Name": "TaskNum",
                        "Value": "010"
                    },
                    {
                        "IsNull": false,
                        "Modified": true,
                        "Name": "EstimatedFinalCost",
                        "Value": item.amount
                    }
                ]
            })
        }
    });

    if (insertPayload.length > 0) {
        console.debug('will insert to SLProjCosts', insertPayload);
    
        return postToSyteline('SLProjCosts', 'additems', insertPayload);
    }
    else {
        return Promise.resolve();
    }
}

//
// SYTELINE WRAPPERS
//

function callSytelineIDO(IDO, propList, projNum, orderBy, filter) {
    return callSyteline(IDO, propList, projNum, orderBy, '', '', filter);
}

function callSytelineCLM(IDO, propList, projNum, orderBy, clm, clmparams) {
    return callSyteline(IDO, propList, projNum, orderBy, clm, clmparams);
}

function callSyteline(IDO, propList, projNum, orderBy, clm, clmparams, filter) {
    return new Promise((resolve, reject) => {
        // REST v2 load: the property list goes in the query string, so long lists are fine
        // (v1 put it in the URL path, which is limited to 260 characters)
        let queryParams = {
            properties: propList.join(','),
            recordCap: 0,
        };

        if (projNum) {
            queryParams.filter = `ProjNum='${projNum}'`;
        }

        if (orderBy) {
            queryParams.orderBy = orderBy;
        }

        if (clm) {
            queryParams.clm = clm;
        }

        if (clmparams) {
            queryParams.clmParam = clmparams;
        }

        if (filter) {
            if (queryParams.filter) {
                queryParams.filter += 'AND ' + filter;
            }
            else {
                queryParams.filter = filter;
            }
        }

        let url = `${config.idoLoadUrl}${IDO}?${queryString.stringify(queryParams)}`;

        axios.get(url)
            .then((response) => {
                if (response.data.Success === false) {
                    return Promise.reject(response);
                }
                // v2 returns each row as an object keyed by property name
                let items = response.data.Items || [];
                console.debug(`Success request: ${IDO}:`, items);
                resolve(items);
            })
            .catch((err) => {
                console.error(`FAILED REQUEST: ${IDO}:`, err);
                reject(err);
            })
    })
}

//
// REQUESTS
//
function putToSyteline(IDO, opKeyword, payload) {
    return new Promise((resolve, reject) => {
        let url = `${IDO}/${opKeyword}`;

        axios.put(url, payload)
            .then((response) => {
                resolve(response);
            })
            .catch((err) => {
                console.error(`FAILED POST REQUEST: ${IDO}:`, err);
                reject(err);
            })
    })
}

function postToSyteline(IDO, opKeyword, payload) {
    return new Promise((resolve, reject) => {
        let url = `${IDO}/${opKeyword}`;

        axios.post(url, payload)
            .then((response) => {
                resolve(response);
            })
            .catch((err) => {
                console.error(`FAILED POST REQUEST: ${IDO}:`, err);
                reject(err);
            })
    })
}

function deleteSyteline(IDO, opKeyword, payload) {
    return new Promise((resolve, reject) => {
        let url = `${IDO}/${opKeyword}`;

        axios.post(url, payload)
            .then((response) => {
                resolve(response);
            })
            .catch((err) => {
                console.error(`FAILED POST REQUEST: ${IDO}:`, err);
                reject(err);
            })
    })
}

function invokeMethodSyteline(IDO, methodname, paramString) {
    return new Promise((resolve, reject) => {
        // Encode the parameter list: project numbers have leading spaces and the list is comma-separated
        let url = `method/${IDO}/${methodname}/?parms=${encodeURIComponent(paramString)}`;

        axios.get(url)
            .then((response) => {
                resolve(response);
            })
            .catch((err) => {
                console.error(`FAILED invokeMethodSyteline REQUEST: ${IDO}:`, err);
                reject(err);
            })
    })
}



export default {
    getProjectsList: getProjectsList,
    getProjectCostDetailReport: getProjectCostDetailReport,
    getProjectBillingsReport: getProjectBillingsReport,
    getProjectPOReport: getProjectPOReport,
    getProjectTaskTens: getProjectTaskTens,
    getProjectCodes: getProjectCodes,
    getMexicanExchangeRates: getMexicanExchangeRates,
    getPOItems: getPOItems,
    getPOItemsForOPNR: getPOItemsForOPNR,
    getProjectCommittedCosts: getProjectCommittedCosts,
    insertEstimatedFinalCostItems: insertEstimatedFinalCostItems,
    updateEstimatedFinalCostItems: updateEstimatedFinalCostItems,
    deleteEstimatedFinalCostItems: deleteEstimatedFinalCostItems,
    invokeProjectTaskBudgetRollup: invokeProjectTaskBudgetRollup,
    
    getProjectDashboardData: getProjectDashboardData,
    updateDashboardData: updateDashboardData,
}